import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { Search, X, Image as ImageIcon, Loader2 } from "lucide-react";

const SOURCES = [["all", "Alle"], ["wikimedia", "Wikimedia Commons"], ["pexels", "Pexels"]];

function ResultCard({ r, busy, onPick }) {
  return (
    <button type="button" disabled={busy} onClick={() => onPick(r)} className="text-left rounded-xl overflow-hidden border bg-white hover:shadow-lg transition-shadow disabled:opacity-50" style={{ borderColor: "var(--wp-border)" }} data-testid={`stock-result-${r.provider}`}>
      <img src={r.thumb_url} alt={r.description || r.id} loading="lazy" className="w-full aspect-square object-cover" />
      <div className="p-2 text-[11px] leading-snug">
        <div className="font-medium truncate">{r.author || "Unbekannt"}</div>
        <div className="opacity-60 truncate">{r.provider === "pexels" ? "Pexels" : "Wikimedia"} · {r.license}</div>
      </div>
    </button>
  );
}

export default function StockPhotoPicker({ plant = {}, onPicked, onImported, query = "", plantId = "", label = "Stockfoto suchen" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const search = async (query = q, src = source) => {
    if (query.trim().length < 2) return;
    setLoading(true);
    try {
      const r = await api.get("/admin/stock-photos/search", { params: { q: query, source: src } });
      setData(r.data);
      (r.data.errors || []).forEach((e) => toast.error(e));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suche fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const openPicker = () => {
    const q0 = query || plant.name_lat || plant.name_de || "";
    setQ(q0);
    setOpen(true);
    if (q0) search(q0, source);
  };

  const pick = async (r) => {
    setSaving(true);
    try {
      if (onImported) {
        const res = await api.post("/admin/stock-photos/import", {
          provider: r.provider, large_url: r.large_url, author: r.author, license: r.license, source_url: r.source_url, plant_id: plantId,
        });
        onImported(res.data.url, res.data.credit);
        toast.success(r.provider === "wikimedia" ? `Foto übernommen – bitte nennen: ${r.author} · ${r.license}` : "Foto übernommen", { duration: 8000 });
        setOpen(false);
        return;
      }
      const res = await api.post(`/admin/plants/${plant.id}/stock-photo`, {
        provider: r.provider, large_url: r.large_url, author: r.author, license: r.license,
        source_url: r.source_url, alt: plant.name_de,
      });
      onPicked(res.data.image_url, res.data.image_credit);
      toast.success("Stockfoto als Hauptbild gesetzt");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Foto konnte nicht übernommen werden");
    } finally {
      setSaving(false);
    }
  };

  if (!onImported && !plant.id) return <div className="text-xs opacity-60 mt-2">Stockfoto-Suche ist nach dem ersten Speichern verfügbar.</div>;

  return (
    <>
      <button type="button" onClick={openPicker} className="mt-2 ml-2 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full font-medium" style={{ backgroundColor: "var(--wp-sand)", color: "var(--wp-forest)" }} data-testid={onImported ? "stock-photo-open-generic" : "stock-photo-open"}>
        <ImageIcon className="w-3.5 h-3.5" /> {label}
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => !saving && setOpen(false)} data-testid="stock-photo-modal">
          <div className="bg-[var(--wp-cream)] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-5 md:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl">Stockfoto{plant.name_de ? ` für ${plant.name_de}` : " suchen"}</h3>
              <button type="button" onClick={() => setOpen(false)} data-testid="stock-photo-close"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); search(); }} className="flex flex-wrap gap-2 mb-3">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Lateinischer Name oder Suchbegriff" className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid="stock-photo-query" />
              <button type="submit" className="btn-primary !py-2 !px-4" data-testid="stock-photo-search"><Search className="w-4 h-4" /> Suchen</button>
            </form>
            <div className="flex flex-wrap gap-2 mb-4">
              {SOURCES.map(([k, label]) => (
                <button key={k} type="button" onClick={() => { setSource(k); search(q, k); }} className={`text-xs px-3 py-1 rounded-full border ${source === k ? "bg-[var(--wp-primary)] text-[var(--wp-cream)]" : "bg-white"}`} style={{ borderColor: "var(--wp-border)" }} data-testid={`stock-source-${k}`}>{label}</button>
              ))}
            </div>
            {data && source !== "wikimedia" && (!data.pexels_enabled || (data.errors || []).some((e) => e.includes("Pexels-Schlüssel"))) && (
              <p className="text-xs mb-3 p-2 rounded" style={{ backgroundColor: "rgba(167,58,58,0.08)", color: "var(--wp-toxic)" }} data-testid="stock-pexels-disabled">
                Pexels ist nicht verbunden ({data.pexels_enabled ? "Schlüssel ungültig" : "Schlüssel fehlt"}) – es werden nur Wikimedia-Fotos angezeigt.
              </p>
            )}
            <p className="text-xs mb-4 opacity-70">Tipp: Wikimedia sortiert nach lateinischem Namen – dort stimmt die Art meistens. Fotograf und Lizenz werden auf der Pflanzenseite klein genannt. Bitte prüfe trotzdem jedes Foto.</p>
            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="stock-photo-results">
                {(data?.results || []).map((r) => <ResultCard key={`${r.provider}-${r.id}`} r={r} busy={saving} onPick={pick} />)}
                {data && data.results.length === 0 && <p className="col-span-full text-sm opacity-70">Keine Fotos gefunden – probier den deutschen oder englischen Namen.</p>}
              </div>
            )}
            {saving && <p className="mt-4 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Foto wird übernommen …</p>}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
